// My space's daily report (roadmap step 9): today's report to review, edit and
// send up the reporting chain, the person's recent ones, and, for a manager, the
// reports their team has sent. Nothing is sent without the person.
import { useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { Avatar, Badge, Button, Icon, Select } from './primitives';
import { PCard } from './people';
import { DateField, Field, TextArea } from './field';
import { EmptyState, InlineStatus, LoadError, useToast } from './feedback';
import { fmtDate, orgTime } from '../lib/dates';
import {
  draftFromTime,
  listMine,
  listTeam,
  saveDraft,
  sendReport,
  type DailyReport,
  type TeamReport,
} from '../lib/dailyReports';
import { listDirectReports, type DirectReport } from '../lib/directReports';

const bodyText = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  fontSize: 'var(--hf-text-md)',
  lineHeight: 'var(--hf-leading-snug)',
  color: 'var(--hf-ink)',
} as const;
const muted = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' } as const;

/** A report in a list: a button that opens its text underneath. */
const ReportRow = ({
  title,
  meta,
  report,
  first,
}: {
  title: ReactNode;
  meta?: ReactNode;
  report: DailyReport;
  first: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: first ? undefined : '1px solid var(--hf-rule)' }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="hf-hit"
        style={{
          // Not `all: unset`: inline, that would also remove the focus ring.
          background: 'none',
          border: 0,
          font: 'inherit',
          textAlign: 'left',
          boxSizing: 'border-box',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-3)',
          padding: 'var(--hf-space-2) 0',
          cursor: 'pointer',
          fontSize: 'var(--hf-text-md)',
          color: 'var(--hf-ink)',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
        </span>
        {meta}
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
      </button>
      {open && <p style={{ ...bodyText, padding: '0 0 var(--hf-space-3)' }}>{report.body}</p>}
    </div>
  );
};

export function MyDailyReport({
  uid,
  today,
  tz,
  dateFormat,
}: {
  uid: string;
  /** The org's today, 'YYYY-MM-DD'. */
  today: string;
  tz: string;
  dateFormat: string | null;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<(DailyReport[] & { failed?: true }) | null>(null);
  const [tries, reload] = useReducer((n: number) => n + 1, 0);
  // null: no draft for today and none started.
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState<'fill' | 'save' | 'send' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    void listMine(uid).then((r) => {
      if (!live) return;
      setRows(r);
      setText(r.find((x) => x.report_date === today)?.body ?? null);
    });
    return () => {
      live = false;
    };
  }, [uid, today, tries]);

  const mine = rows?.find((r) => r.report_date === today);
  const history = (rows ?? []).filter((r) => r.report_date !== today);
  const label = 'Daily report';

  if (!rows)
    return (
      <PCard label={label}>
        <div style={muted}>Loading…</div>
      </PCard>
    );
  if (rows.failed)
    return (
      <PCard label={label}>
        <LoadError what="your daily report" onRetry={reload} />
      </PCard>
    );

  const start = async () => {
    setBusy('fill');
    setText(await draftFromTime(uid, today));
    setBusy(null);
  };
  const save = async (): Promise<boolean> => {
    if (text === null) return false;
    const err = await saveDraft(uid, today, text);
    setError(err ?? '');
    return !err;
  };
  const onSave = async () => {
    setBusy('save');
    if (await save()) {
      toast.push({ tone: 'success', message: 'Draft saved' });
      reload();
    }
    setBusy(null);
  };
  const onSend = async () => {
    setBusy('send');
    const ok = text === mine?.body || (await save());
    const err = ok ? await sendReport(today) : null;
    if (err) setError(err);
    if (ok && !err) {
      toast.push({ tone: 'success', message: 'Report sent' });
      reload();
    }
    setBusy(null);
  };

  return (
    <PCard
      label={label}
      right={<span style={muted}>{fmtDate(today, dateFormat, { weekday: true })}</span>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
        {mine?.status === 'sent' ? (
          <>
            <p style={bodyText}>{mine.body}</p>
            <div>
              <Badge tone="success" dot>
                Sent {mine.sent_at ? orgTime(mine.sent_at, tz) : ''}
              </Badge>
            </div>
          </>
        ) : text === null ? (
          <EmptyState
            title="No report for today yet"
            action={
              <Button variant="primary" size="sm" onClick={start} loading={busy === 'fill'}>
                Start a draft
              </Button>
            }
          >
            Your assistant may draft one for you. Or start it here from today’s check-out summary
            and project time.
          </EmptyState>
        ) : (
          <>
            <Field
              label="Today’s report"
              hint={
                mine?.drafted_by === 'agent'
                  ? 'Drafted by your assistant. Check it, edit it, then send.'
                  : undefined
              }
            >
              <TextArea value={text} onChange={setText} autoResize rows={6} />
            </Field>
            {error && <InlineStatus tone="error">{error}</InlineStatus>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-2)' }}>
              <Button
                variant="primary"
                size="sm"
                icon="arrowRight"
                onClick={onSend}
                loading={busy === 'send'}
                disabled={!!busy || !text.trim()}
              >
                Send
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                loading={busy === 'save'}
                disabled={!!busy || text === mine?.body}
              >
                Save draft
              </Button>
            </div>
            <div style={muted}>
              Sending shares it with everyone above you. A sent report can’t be changed.
            </div>
          </>
        )}
        {history.length > 0 && (
          <div>
            <div style={{ ...muted, margin: 'var(--hf-space-2) 0' }}>Recent</div>
            {history.map((r, i) => (
              <ReportRow
                key={r.report_date}
                first={i === 0}
                report={r}
                title={fmtDate(r.report_date, dateFormat, { weekday: true })}
                meta={
                  <Badge tone={r.status === 'sent' ? 'success' : 'neutral'}>
                    {r.status === 'sent' ? 'Sent' : 'Draft'}
                  </Badge>
                }
              />
            ))}
          </div>
        )}
      </div>
    </PCard>
  );
}

/**
 * Reports sent by the people below this viewer (skip-levels included). Renders
 * nothing for someone nobody reports to.
 */
export function TeamReports({
  uid,
  tz,
  dateFormat,
}: {
  uid: string;
  tz: string;
  dateFormat: string | null;
}) {
  const [team, setTeam] = useState<(DirectReport[] & { failed?: true }) | null>(null);
  const [rows, setRows] = useState<(TeamReport[] & { failed?: true }) | null>(null);
  const [tries, retry] = useReducer((n: number) => n + 1, 0);
  const [person, setPerson] = useState('');
  const [day, setDay] = useState('');
  useEffect(() => {
    let live = true;
    void Promise.all([listDirectReports(uid), listTeam(uid)]).then(([t, r]) => {
      if (!live) return;
      setTeam(t);
      setRows(r);
    });
    return () => {
      live = false;
    };
  }, [uid, tries]);

  const people = useMemo(
    () =>
      [...new Map((rows ?? []).map((r) => [r.profile_id, r.name ?? 'Someone'])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1]),
      ),
    [rows],
  );
  if (!team || !rows) return null;
  const label = 'Team reports';
  if (team.failed || rows.failed)
    return (
      <PCard label={label}>
        <LoadError what="your team’s reports" onRetry={retry} />
      </PCard>
    );
  if (!team.length && !rows.length) return null;
  const shown = rows.filter(
    (r) => (!person || r.profile_id === person) && (!day || r.report_date === day),
  );

  return (
    <PCard label={label}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 'var(--hf-space-2)',
          marginBottom: 'var(--hf-space-2)',
        }}
      >
        <div style={{ flex: '1 1 160px' }}>
          <Select
            ariaLabel="Person"
            value={person}
            onChange={setPerson}
            options={[
              { value: '', label: 'Everyone' },
              ...people.map(([value, name]) => ({ value, label: name })),
            ]}
          />
        </div>
        <DateField label="Date" dense value={day} onChange={setDay} style={{ flex: '1 1 160px' }} />
      </div>
      {shown.length === 0 ? (
        <EmptyState title={rows.length ? 'No reports match these filters' : 'No reports sent yet'}>
          {rows.length ? undefined : 'Reports your team sends you show here.'}
        </EmptyState>
      ) : (
        shown.map((r, i) => (
          <ReportRow
            key={`${r.profile_id}:${r.report_date}`}
            first={i === 0}
            report={r}
            title={
              <>
                <Avatar name={r.name ?? undefined} size={22} />
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{r.name ?? 'Someone'}</span>
              </>
            }
            meta={
              <span style={muted}>
                {fmtDate(r.report_date, dateFormat, { weekday: true })}
                {r.sent_at ? ` · ${orgTime(r.sent_at, tz)}` : ''}
              </span>
            }
          />
        ))
      )}
    </PCard>
  );
}
